/**
 * Which wiki file-node uploads are in flight IN THIS TAB, right now.
 *
 * Exists because `wiki_nodes.file_s3_version_id IS NULL` means two very
 * different things and the difference is invisible in the data:
 *
 *   1. **Uploading now** — presign has created the row and the S3 PUT is
 *      still running. The row should read as busy.
 *   2. **Abandoned** — the PUT died. A refresh, a closed tab, a dropped
 *      connection, a sleeping laptop. Nothing is happening and nothing
 *      ever will; the row is a ghost until the reaper retires it.
 *
 * The server cannot tell them apart at read time (both are just a null
 * version id) and neither can a freshly-loaded page. Only the tab that
 * owns the PUT knows, so that tab records it here and the tree asks.
 *
 * Deliberately module-scope rather than React state: the uploader
 * (`useFileNodeUpload`) and the reader (`WikiTreeNode`, via
 * `useUploadingNodeIds`) are in unrelated parts of the tree, the set has
 * to survive the uploading component unmounting mid-PUT, and it must be
 * readable from a `beforeunload` handler that has no React context.
 *
 * Scope is the TAB, which is the honest scope: a second tab genuinely
 * does not know whether the first one is still uploading, and showing it
 * "Upload didn't finish" would be a guess. It shows the same thing a
 * reloaded page shows, which is the truthful answer from where it sits.
 */

import type { PutProgress } from './put-with-progress'

type Listener = () => void

/**
 * Key → most recent progress for that upload. A `Map` rather than the
 * original `Set` so a surface can draw a bar; membership still answers
 * "is anything in flight", which is all the unload guard asks.
 *
 * `null` progress means registered but no bytes reported yet — presign has
 * returned and the transfer has not started ticking. Surfaces render that
 * as indeterminate rather than as 0%.
 */
const inFlight = new Map<string, PutProgress | null>()
const listeners = new Set<Listener>()

/**
 * Snapshot handed to `useSyncExternalStore`. Cached and only replaced when
 * the map actually changes, because the store contract requires a stable
 * reference between notifications — returning a fresh array each call
 * makes React re-render forever.
 */
let snapshot: readonly string[] = []

/**
 * Progress snapshot, kept SEPARATE from the id snapshot on purpose.
 *
 * Progress ticks many times a second, and the id list changes only when an
 * upload starts or ends. Sharing one snapshot would re-render every
 * consumer of the id list (the whole wiki tree, via `useUploadingNodeIds`)
 * on every byte. Two references mean a consumer subscribes to the one it
 * actually needs.
 */
let progressSnapshot: ReadonlyMap<string, PutProgress | null> = new Map()

/** Notify for a membership change — an upload started or finished. */
const emit = () => {
  snapshot = [...inFlight.keys()]
  progressSnapshot = new Map(inFlight)
  for (const listener of listeners) listener()
}

/** Notify for a progress tick — membership is unchanged. */
const emitProgressOnly = () => {
  progressSnapshot = new Map(inFlight)
  for (const listener of listeners) listener()
}

/** Mark an upload as started. Call right after presign mints the node id. */
export const markUploadStarted = (nodeId: string): void => {
  if (inFlight.has(nodeId)) return
  inFlight.set(nodeId, null)
  emit()
}

/**
 * Mark an upload as no longer running — success, failure or abort alike.
 * MUST be called on every exit path (a `finally`), or the row stays busy
 * forever and the unload guard nags on a tab with nothing in flight.
 */
export const markUploadFinished = (nodeId: string): void => {
  if (!inFlight.delete(nodeId)) return
  emit()
}

/** True while this tab is actively PUTting bytes for `nodeId`. */
export const isUploadInFlight = (nodeId: string): boolean => inFlight.has(nodeId)

/**
 * Record how far an upload has got. No-op for a key that is not in flight,
 * so a late tick arriving after cancellation cannot resurrect a finished
 * entry and leave the unload guard warning forever.
 *
 * Callers should coalesce before calling this — see `throttleProgress`.
 */
export const updateUploadProgress = (nodeId: string, progress: PutProgress): void => {
  if (!inFlight.has(nodeId)) return
  inFlight.set(nodeId, progress)
  emitProgressOnly()
}

/** Latest progress for one upload, or null if unknown / not started. */
export const getUploadProgress = (nodeId: string): PutProgress | null =>
  inFlight.get(nodeId) ?? null

/** How many uploads this tab currently owns. Drives the unload guard. */
export const inFlightUploadCount = (): number => inFlight.size

export const subscribeToUploads = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getUploadSnapshot = (): readonly string[] => snapshot

export const getProgressSnapshot = (): ReadonlyMap<string, PutProgress | null> => progressSnapshot

/**
 * Wrap a progress callback so it fires only when the whole percent
 * changes (and always on the final byte).
 *
 * `xhr.upload.onprogress` fires many times a second on a fast link. Every
 * call here re-renders each subscriber, so an unthrottled feed makes a
 * large upload re-render the tree hundreds of times for changes no one can
 * see. Whole-percent granularity is the most a progress bar can express.
 */
export const throttleProgress = (
  onProgress: (progress: PutProgress) => void,
): ((progress: PutProgress) => void) => {
  let lastPercent = -1
  return (progress) => {
    const percent = progress.total > 0 ? Math.floor((progress.loaded / progress.total) * 100) : 0
    const isFinal = progress.total > 0 && progress.loaded >= progress.total
    if (percent === lastPercent && !isFinal) return
    lastPercent = percent
    onProgress(progress)
  }
}

/** Test-only. Clears the registry between cases. */
export const __resetUploadRegistryForTests = (): void => {
  inFlight.clear()
  snapshot = []
  progressSnapshot = new Map()
  listeners.clear()
}
